import React, { FC, SyntheticEvent, useCallback, useState } from 'react';
import { QuickQuoteAvgTime } from './QuickQuoteLabel';
import { QuickQuoteFields, QuickQuoteFormProps, QuickQuoteFormLabels, QuickQuoteTraveller } from './QuickQuoteTypes';
import { GenericDatePicker, GenericDropdown, GenericHidden, GenericToggle } from '../../shared/components';
import { Travellers } from './QuickQuoteFields';
import validate from './validate';
import { Message, usePageTitle, Progress } from '@trip-ux/components';
import { Button } from '@manulife/mux';
import { get, cloneDeep, map, isEmpty, set, isString } from 'lodash';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { useSelector } from 'react-redux';
import { FormApi } from 'final-form';
import {
  ReduxStore,
  SALES_CHANNEL,
  useActions,
  setQuoteForm,
  resetStore,
  setTripTravellerType
} from '@trip-ux/redux-store';
import { Form, Field } from 'react-final-form';
import { labelsSelector } from '../../utils';
import { AUTH_ROLES, CAN, PERMISSIONS, POLICY_ROUTES, QUOTE_FORM_CONSTANTS } from '@trip-ux/constants';
import { checkTrue, safeQueryString, converToNumber, dayjs } from '@trip-ux/common';
import { WithRole } from '../../shared/WithRole';

const plusYearMinusDay = (day: Date) => {
  day.setFullYear(day.getFullYear() + 1);
  day.setDate(day.getDate() - 1);
  return day;
};

const multiTripDays = 364;

const getToggleValue = value => checkTrue(value);

const setReturnDateFromMultiTrip = (isMultiTrip: boolean, deptDate: Date, format: string, cbFn: (result) => void) => {
  if (isMultiTrip) {
    cbFn(dayjs(deptDate).add(multiTripDays, 'day').format(format));
  } else {
    cbFn('');
  }
};

const setDepartureDateFromMultiTrip = (
  isMultiTrip: boolean,
  departureDate: string,
  format: string,
  cbFn: (result) => void
) => {
  if (isMultiTrip && isEmpty(departureDate)) {
    cbFn(dayjs().format(format));
  }
};

const getReturnDateFromDeptDate = (
  isMultiTrip: boolean,
  deptDateString: string,
  format: string,
  cbFn: (result) => void
) => {
  // String type check to ignore invalid date string values
  if (isString(deptDateString)) {
    let result = '';
    if (!isEmpty(deptDateString) && isMultiTrip) {
      const deptDate = new Date(deptDateString);
      result = dayjs(deptDate).add(multiTripDays, 'day').format(format);
    } else {
      result = '';
    }

    cbFn(result);
  }
};

const setDeptDateState = (deptDate: string, cbFn: (result) => void) => {
  if (isString(deptDate)) {
    const newDeptDate = new Date(deptDate);
    cbFn(newDeptDate);
  }
};

const prepareTravellers = (travellers: QuickQuoteTraveller[]) => {
  return map(travellers, val => ({
    ...val,
    tripCost: converToNumber(val.tripCost)
  }));
};

const getDatePickerLabels = (fieldName: string, labels: QuickQuoteFormLabels) => ({
  ...labels,
  calendarLabels: {
    ...labels.calendarLabels,
    textBoxAriaLabel: labels.formLabels[fieldName]
  }
});

export const QuickQuoteForm: FC<
  QuickQuoteFormProps & {
    initialValues: Partial<QuickQuoteFields>;
    disableSubmit: boolean;
    labels: QuickQuoteFormLabels;
    form: FormApi<QuickQuoteFields>;
    values: Partial<QuickQuoteFields>;
  }
> = ({ labels, submitAdminForm, config, disableSubmit, values, form }) => {
  const [submitting, setSubmitting] = useState(false);
  const actions = useActions({ setQuoteForm, resetStore, setTripTravellerType });
  const isB2B = config.options.salesChannel === SALES_CHANNEL.B2B;

  const formCallback = useCallback(
    (e: SyntheticEvent) => {
      const submitValue = cloneDeep(values);
      submitValue.travellers = prepareTravellers(get(submitValue, 'travellers'));
      actions.resetStore();
      actions.setQuoteForm(submitValue);
      actions.setTripTravellerType();
      if (config.isAdmin || disableSubmit) {
        e.preventDefault();
        e.stopPropagation();
      } else {
        setSubmitting(true);
      }
      config.isAdmin && !disableSubmit && submitAdminForm();
    },
    [config.isAdmin, disableSubmit, submitAdminForm, values]
  );

  const [deptDate, setDeptDate] = useState(new Date());
  const [isMultiTrip, setIsMultiTrip] = useState(checkTrue(values?.multiTrip));
  const handleDeptDateChange = value => {
    setDeptDateState(value, result => setDeptDate(result));
    getReturnDateFromDeptDate(isMultiTrip, value, labels.misc.dateFormat, (result: string) =>
      form.change('returnDate', result)
    );
  };

  const [country, setCountry] = useState(undefined);
  const handleCountryChange = value => {
    setCountry(value);
    if (value !== CAN) {
      form.change('travelOutsideCanada', 'false');
    }
  };

  const handleTravelOutsideCanadaChange = e => {
    if (country && country !== CAN && checkTrue(e.target.value)) {
      e.preventDefault();
    }
  };

  const handleMultiTripChange = e => {
    const toggleValue = getToggleValue(e.target.value);
    setIsMultiTrip(toggleValue);

    setReturnDateFromMultiTrip(toggleValue, deptDate, labels.misc.dateFormat, result =>
      form.change('returnDate', result)
    );
    const formState = form.getState();
    const departureDate = get(formState, 'values.departureDate');
    setDepartureDateFromMultiTrip(toggleValue, departureDate, labels.misc.dateFormat, result =>
      form.change('departureDate', result)
    );
  };

  return (
    <>
      {submitting && <Progress backdrop />}
      <form
        className="quote-form"
        // eslint-disable-next-line no-restricted-globals
        action={`${config.options.contextPath}${POLICY_ROUTES.BOOKING}?${safeQueryString(location.search)}`}
        method="post">
        <div className="quote-form-row grey-light-brd">
          <QuickQuoteAvgTime {...labels.avgTime} />
          <div className="d-flex justify-content-between align-items-end flex-wrap">
            <div className="row-ele my-3">
              <div className="message-white-space-desktop" />
              <Field
                id="departureDate-field"
                key={`departureDate-field-multitrip-${isMultiTrip}`}
                name="departureDate"
                component={GenericDatePicker}
                labels={getDatePickerLabels('departureDate', labels)}
                dateFormat={labels.misc.dateFormat}
                disabledDays={{ before: new Date(), after: plusYearMinusDay(new Date()) }}
                inputOnChange={handleDeptDateChange}
              />
            </div>
            <div className="row-ele my-3">
              <div className="message-white-space-desktop" />
              <Field
                id="returnDate-field"
                key={`returnDate-field-multitrip-${isMultiTrip}-${dayjs(deptDate).format(labels.misc.dateFormat)}`}
                name="returnDate"
                readOnly={isMultiTrip}
                component={GenericDatePicker}
                labels={getDatePickerLabels('returnDate', labels)}
                defaultFocus={deptDate}
                dateFormat={labels.misc.dateFormat}
                disabledDays={{ before: deptDate }}
              />
            </div>
            <div className="row-ele my-3">
              <Message variant="secondary-info">
                <p className="driving-message">{labels.misc.ifDrivingMessage}</p>
              </Message>
              <Field
                name="bookingDate"
                component={GenericDatePicker}
                labels={getDatePickerLabels('bookingDate', labels)}
                dateFormat={labels.misc.dateFormat}
                disabledDays={{ after: new Date() }}
              />
            </div>
            {isB2B && (
              <div className="row-ele my-3">
                <div className="message-white-space-tablet" />
                <Field
                  name="country"
                  component={GenericDropdown}
                  labels={labels}
                  options={labels.misc.countryOptions}
                  placeholder={labels.formLabels.countryPlaceholder}
                  inputOnChange={handleCountryChange}
                />
              </div>
            )}
            {!isB2B && <Field name="country" component={GenericHidden} />}
            <div className="row-ele my-3">
              <div className="message-white-space-tablet" />
              <Field
                id="province-field"
                name="province"
                component={GenericDropdown}
                labels={labels}
                options={labels.misc.provinceOptions}
                placeholder={labels.formLabels.provincePlaceholder}
              />
            </div>
            <div data-testid="travelOutsideCanada-container" className="row-ele my-3">
              <Field
                name="travelOutsideCanada"
                component={GenericToggle}
                labels={labels}
                options={labels.misc.yesNoOptions}
                verticalWidth={QUOTE_FORM_CONSTANTS.TOGGLE_MAX_Y_WIDTH}
                inputOnChange={handleTravelOutsideCanadaChange}
              />
            </div>
            <div data-testid="multiTrip-container" className="row-ele my-3">
              {isMultiTrip ? (
                <Message variant="secondary-info">
                  <p className="wrapped-message">{labels.misc.multiTripMessage}</p>
                </Message>
              ) : null}
              <Field
                name="multiTrip"
                component={GenericToggle}
                labels={labels}
                options={labels.misc.yesNoOptions}
                verticalWidth={QUOTE_FORM_CONSTANTS.TOGGLE_MAX_Y_WIDTH}
                inputOnChange={handleMultiTripChange}
              />
            </div>
          </div>
        </div>
        <div className="quote-form-row quote-travellers grey-light-brd">
          <Message variant="secondary-info">
            <p className="family-message">{labels.misc.familyPlanMessage}</p>
          </Message>
          <FieldArray
            name="travellers"
            rerenderOnEveryChange={true}
            component={Travellers}
            labels={labels}
            config={config}
          />
        </div>
        <div className="quote-form-row grey-light-brd">
          <div className="button-row d-flex justify-content-between flex-wrap">
            <div className="row-ele my-3">
              <Field
                name="familyPlans"
                component={GenericToggle}
                labels={labels}
                options={labels.misc.yesNoOptions}
                verticalWidth={QUOTE_FORM_CONSTANTS.TOGGLE_MAX_Y_WIDTH}
              />
            </div>
            <WithRole.Component
              roles={AUTH_ROLES.QUOTE_READ}
              render={({ hasRole }) => (
                <Button
                  id="quick-quote-submit"
                  disabled={disableSubmit || !hasRole}
                  type="submit"
                  className="my-3"
                  onClick={formCallback}>
                  {labels.formLabels.viewResult}
                </Button>
              )}
            />
          </div>
        </div>
      </form>
    </>
  );
};

export const QuickQuote: FC<{ submitAdminForm?: () => void }> = ({ submitAdminForm }) => {
  const { labels, config, fieldValues, page } = useSelector((store: ReduxStore) => ({
    labels: get(labelsSelector(store), 'quote_form'),
    page: get(labelsSelector(store), 'page'),
    config: store.config,
    fieldValues: store.quoteForm.form
  }));

  usePageTitle(page.title.quickQuote);

  const initial = {
    departureDate: '',
    returnDate: '',
    bookingDate: '',
    country: config.options.salesChannel === SALES_CHANNEL.B2B ? '' : CAN,
    province: '',
    travelOutsideCanada: 'false',
    multiTrip: 'false',
    familyPlans: 'false',
    travellers: [{}]
  };

  const initialValue = fieldValues ? fieldValues : initial;

  return (
    <WithRole.Route roles={PERMISSIONS.BOOKING.BOOK_QUOTE}>
      <Form<QuickQuoteFields>
        onSubmit={() => {
          //Nothing to do
        }}
        validate={values => validate(values, { labels })}
        mutators={{ ...arrayMutators }}
        initialValues={initialValue}
        render={({ submitting, hasValidationErrors, form, values }) => (
          <QuickQuoteForm
            initialValues={initialValue}
            disableSubmit={submitting || hasValidationErrors}
            labels={labels}
            config={config}
            form={form}
            values={values}
            submitAdminForm={submitAdminForm}
          />
        )}
      />
    </WithRole.Route>
  );
};
QuickQuote.displayName = 'QuickQuote';
export default QuickQuote;
